import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { LangSwitcher } from './LangSwitcher';
import { LocalizedLink } from './LocalizedLink';
import { isSubdomainEnabled, getAppDomain } from '../utils/domain';
import { I18N_ROUTING_ENABLED, type AppLocale } from '../i18n';
import { localePrefix } from '../i18n/slugMap';

/** Itens do menu principal — âncoras de seções da Landing (home).
 *  `labelKey`/`labelPt`: chave i18n + texto PT-BR default (fallback). */
const NAV_LINKS = [
  { labelKey: 'nav.competitions', labelPt: 'Competições', hash: '#competicoes' },
  { labelKey: 'nav.how_it_works', labelPt: 'Como Funciona', hash: '#como-funciona' },
  { labelKey: 'nav.features', labelPt: 'Funcionalidades', hash: '#funcionalidades' },
  { labelKey: 'nav.scoring', labelPt: 'Pontuação', hash: '#pontuacao' },
  { labelKey: 'nav.simulators', labelPt: 'Simuladores', hash: '#simuladores' },
  { labelKey: 'nav.get_app', labelPt: 'Baixe o App', hash: '#app' },
  { labelKey: 'nav.faq', labelPt: 'FAQ', hash: '#faq' },
];

/**
 * Header público unificado — usado na Landing e em todas as páginas públicas
 * (institucionais, blog, etc.). O menu aponta para seções da home: se já
 * estiver na home, faz scroll suave; senão, navega para `/#ancora` (a Landing
 * tem um efeito que lê o hash e rola até a seção).
 */
export function PublicHeader() {
  const { t, i18n } = useTranslation('common');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const locale = (i18n.language || 'pt-BR') as AppLocale;
  // Prefix do idioma corrente quando i18n routing está ativo (em PROD com a
  // flag OFF, prefix sempre é ''). Usado pra montar `loginUrl` e o destino
  // das âncoras de seção da home (`/#xx` ou `/en#xx`/`/es#xx`).
  const prefix = I18N_ROUTING_ENABLED ? localePrefix(locale) : '';
  const homePath = prefix || '/';
  // Pós-Sessão F: as rotas autenticadas têm variantes /en/auth e /es/auth
  // (mesmo slug em todos os idiomas; o prefix faz o detector de URL do i18next
  // já carregar a tela /auth no idioma certo no F5/refresh). Antes era só
  // /auth sem prefix e o detector caía no cookie/localStorage como fallback.
  const loginUrl = isSubdomainEnabled()
    ? `https://${getAppDomain()}${prefix}/auth`
    : `${prefix}/auth`;

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    // Se já estamos na home do idioma corrente (`/` ou `/en` ou `/es`), só
    // faz scroll. Senão, navega pra home do idioma + hash (React Router →
    // sem reload, preserva idioma).
    const onHome = location.pathname === homePath || location.pathname === `${homePath}/`;
    if (onHome) {
      document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`${homePath}${hash}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo */}
          <LocalizedLink to="/" className="flex items-center gap-2 shrink-0" aria-label={t('header.home_aria', 'Bolão Elite — início')}>
            <Logo showText={true} imgClassName="h-11 md:h-14 w-auto object-contain" />
          </LocalizedLink>

          {/* Nav central — desktop */}
          <nav className="hidden md:flex items-center gap-7 mx-auto">
            {NAV_LINKS.map((link) => (
              <a
                key={link.hash}
                href={`${homePath}${link.hash}`}
                onClick={(e) => handleNav(e, link.hash)}
                className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
              >
                {t(link.labelKey, link.labelPt)}
              </a>
            ))}
          </nav>

          {/* Idioma + botão — desktop */}
          <div className="hidden md:flex items-center gap-4 shrink-0">
            <LangSwitcher />
            <a
              href={loginUrl}
              className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-sm"
            >
              {t('auth.login', 'Entrar')}
            </a>
          </div>

          {/* Hamburger — mobile */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden ml-auto p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label={t('header.menu', 'Menu')}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.hash}
                href={`${homePath}${link.hash}`}
                onClick={(e) => handleNav(e, link.hash)}
                className="block px-2 py-2.5 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                {t(link.labelKey, link.labelPt)}
              </a>
            ))}
            <div className="pt-3 space-y-3">
              <div className="flex justify-center">
                <LangSwitcher />
              </div>
              <a
                href={loginUrl}
                onClick={() => setMobileMenuOpen(false)}
                className="w-full block text-center py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all"
              >
                {t('auth.login', 'Entrar')}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default PublicHeader;
